/**
 * The easing picker's grid (W2-05; DESIGN.md §4.12): [Linear] [Stay, then jump] on top, then one row per family with
 * its plain strength word and the columns Slow end / Slow start / Slow both. Each cell shows Phira's curve (a
 * thumbnail that animates on hover unless motion is reduced), its RPE number (always at the Full level, else on
 * hover / focus), and badges: 23 / 29 "Phira draws this differently from RPE", the fitted Bezier cells with their
 * error, favourites, "baked on export", text overshoot warnings. Refused cells stay focusable and say why.
 *
 * Presentational: focus (roving tabindex), hover and picking are reported to the picker, which owns the keyboard.
 */

import { Fragment } from 'react';
import { Icon } from '../../ui';
import { CurveThumb } from '../../ui';
import { m } from './easing.messages';
import s from './Easing.module.css';
import { useText, type Text } from './hooks';
import { GRID_SHAPES, GRID_STRENGTHS, pickerGrid, topRow, type CellState, type PickerCell } from './pickerModel';

export interface EasingGridProps {
  readonly focus: { readonly row: number; readonly col: number };
  /** Cell of the segment's current curve. */
  readonly current: string | null;
  readonly showIds: boolean;
  readonly stateOf: (cell: PickerCell) => CellState;
  readonly isFavourite: (cell: PickerCell) => boolean;
  readonly cellRef: (id: string, el: HTMLButtonElement | null) => void;
  onFocusCell(cell: PickerCell): void;
  onHover(cell: PickerCell | null): void;
  onPick(cell: PickerCell): void;
}

/** Accessible / tooltip name of a cell. */
export function cellName(cell: PickerCell, text: Text): string {
  const { t } = text;
  if (cell.id === 'linear') return t(m.linear);
  if (cell.id === 'hold') return t(m.hold);
  const strength = t(`ease.strength.${GRID_STRENGTHS[cell.row] ?? 'gentle'}`);
  const shape = t(`ease.shape.${GRID_SHAPES[cell.col] ?? 'slowEnd'}`);
  if (cell.approx !== undefined) {
    return t(m.cellApprox, {
      strength,
      shape,
      name: cell.approx.family === 'quint' ? 'InOutQuint' : 'InOutExpo',
      error: (Math.round(cell.approx.maxError * 1000) / 10).toFixed(1),
    });
  }
  return t(m.cellNameFull, { strength, shape, rpeName: cell.entry?.rpeName ?? '', id: cell.entry?.easingType ?? 0 });
}

function Cell(props: EasingGridProps & { readonly cell: PickerCell; readonly text: Text }) {
  const { cell, text } = props;
  const { t, tr } = text;
  const state = props.stateOf(cell);
  const focused = props.focus.row === cell.row && props.focus.col === cell.col;
  const disabled = state.kind === 'disabled';
  const name = cellName(cell, text);
  const tips: string[] = [name];
  if (disabled) tips.push(tr(state.reason));
  if (cell.entry?.differsInRpe === true) tips.push(t(m.differsTip));
  if (cell.approx !== undefined) {
    tips.push(
      t(m.approxTip, {
        name: cell.approx.family === 'quint' ? 'InOutQuint' : 'InOutExpo',
        error: (Math.round(cell.approx.maxError * 1000) / 10).toFixed(1),
      }),
    );
  }
  if (state.kind === 'ok' && state.warning !== undefined) tips.push(tr(state.warning));
  if (state.kind === 'ok' && state.baked === true) tips.push(t(m.bakedTip));
  const id = cell.entry?.easingType;
  const overshoots = cell.entry?.overshoots === true;
  return (
    <div role="gridcell" className={s.gridCell}>
      <button
        type="button"
        ref={(el) => {
          props.cellRef(cell.id, el);
        }}
        className={s.cell}
        tabIndex={focused ? 0 : -1}
        aria-label={tips.join('. ')}
        aria-disabled={disabled || undefined}
        aria-current={props.current === cell.id ? 'true' : undefined}
        title={tips.join('\n')}
        data-cell={cell.id}
        data-focused={focused || undefined}
        data-autofocus={focused || undefined}
        onFocus={() => {
          props.onFocusCell(cell);
        }}
        onPointerEnter={() => {
          props.onHover(cell);
        }}
        onPointerLeave={() => {
          props.onHover(null);
        }}
        onClick={() => {
          if (!disabled) props.onPick(cell);
        }}
      >
        <CurveThumb ease={cell.ease} size={cell.row === -1 ? 28 : 36} showOvershoot={overshoots} />
        {cell.row === -1 && <span className={s.cellText}>{cell.id === 'linear' ? t(m.linear) : t(m.hold)}</span>}
        {id !== undefined && cell.row !== -1 && (
          <span className={s.cellId} data-always={props.showIds || undefined}>
            {id}
          </span>
        )}
        <span className={s.cellBadges} aria-hidden="true">
          {cell.approx !== undefined && <Icon name="WandSparkles" size={12} />}
          {cell.entry?.differsInRpe === true && <Icon name="Info" size={12} />}
          {state.kind === 'ok' && state.warning !== undefined && <Icon name="TriangleAlert" size={12} />}
          {disabled && <Icon name="Ban" size={12} />}
          {props.isFavourite(cell) && <Icon name="Star" size={12} />}
        </span>
      </button>
    </div>
  );
}

export function EasingGrid(props: EasingGridProps) {
  const text = useText();
  const { t } = text;
  return (
    <div
      role="grid"
      aria-label={t(m.gridLabel)}
      className={s.grid}
      onPointerLeave={() => {
        props.onHover(null);
      }}
    >
      <div role="row" className={s.topRow} aria-label={t(m.topRowLabel)}>
        {topRow().map((cell) => (
          <Cell key={cell.id} {...props} cell={cell} text={text} />
        ))}
      </div>
      <div role="row" className={s.gridHead}>
        <span role="columnheader" className={s.rowHead} />
        {GRID_SHAPES.map((shape) => (
          <span key={shape} role="columnheader" className={s.colHead}>
            {t(`ease.shape.${shape}`)}
          </span>
        ))}
      </div>
      {pickerGrid().map((row, r) => {
        const strength = GRID_STRENGTHS[r] ?? 'gentle';
        return (
          <Fragment key={strength}>
            <div role="row" className={s.gridRow}>
              <span role="rowheader" className={s.rowHead}>
                {t(`ease.strength.${strength}`)}
              </span>
              {row.map((cell) => (
                <Cell key={cell.id} {...props} cell={cell} text={text} />
              ))}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
